import { ModTag } from '@moderneinc/design-system-components';
import {
  borderRadius,
  iconSize,
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { ButtonBase } from '@mui/material';
import Link from 'next/link';
import type { FunctionComponent, ReactNode } from 'react';
import { navBetaTagSx } from '../../../constants/sx';
import { lineHeight } from '../../../constants/typography';
import { alphaVar } from '../../../helpers/alpha.helper';

type OverflowMenuRowProps = {
  icon?: ReactNode;
  label: string;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
  selected?: boolean;
  tag?: string;
  beta?: boolean;
  indent?: boolean;
  'data-testid'?: string;
};

export const OverflowMenuRow: FunctionComponent<OverflowMenuRowProps> = ({
  icon,
  label,
  href,
  target,
  rel,
  onClick,
  selected = false,
  tag,
  beta = false,
  indent = false,
  'data-testid': dataTestId
}) => {
  const tagToRender = beta ? (tag ?? 'Beta') : tag;

  return (
    <ButtonBase
      component={href ? Link : 'button'}
      href={href}
      target={target}
      rel={rel}
      onClick={onClick}
      data-testid={dataTestId}
      disableRipple
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        gap: `${spacing.spacing_1_1_2}px`,
        paddingTop: `${spacing.spacing_1}px`,
        paddingBottom: `${spacing.spacing_1}px`,
        paddingLeft: indent
          ? `${spacing.spacing_4}px`
          : `${spacing.spacing_1_1_2}px`,
        paddingRight: `${spacing.spacing_1_1_2}px`,
        borderRadius: `${borderRadius.s}px`,
        textAlign: 'left',
        textDecoration: 'none',
        color: semanticColors.color.text.primary,
        fontSize: typography.fontSize.caption,
        lineHeight: `${lineHeight.m}px`,
        fontWeight: typography.fontWeight.regular,
        // Selection reads as a tint over the menu surface, matching the left nav
        // rail. The old `digitalBlue[50]/[100]` are primitives that don't flip,
        // so the selected row stayed near-white in dark mode.
        backgroundColor: selected
          ? alphaVar(semanticColors.control.bgChecked, 0.1)
          : 'transparent',
        '&:hover': {
          backgroundColor: selected
            ? alphaVar(semanticColors.control.bgChecked, 0.2)
            : semanticColors.color.surface.rowHover
        },
        '& > .overflow-menu-row__icon': {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          color: semanticColors.color.icon.default,
          flexShrink: 0,
          '& > svg': {
            width: iconSize.lg,
            height: iconSize.lg
          }
        },
        '& > .overflow-menu-row__label': {
          flex: 1,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      }}
    >
      {!indent && icon ? (
        <span className="overflow-menu-row__icon">{icon}</span>
      ) : null}
      <span className="overflow-menu-row__label">{label}</span>
      {tagToRender ? (
        <ModTag
          label={tagToRender}
          size="small"
          variant={beta ? 'filled' : 'outlined'}
          intent={beta ? 'beta' : 'default'}
          sx={beta ? navBetaTagSx : undefined}
        />
      ) : null}
    </ButtonBase>
  );
};
